import {
  Injectable,
  NgZone,
  inject,
  OnDestroy,
  PLATFORM_ID,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { ConnectionState } from "./websocket-base.service";
import { environment } from "../../../environments/environment";

export { ConnectionState };

interface WebSocketEventMessage {
  id?: string;
  type: string;
  event?: any;
}

/**
 * WebSocket service for AppSync Event API (non-GraphQL WebSocket).
 * Handles native WebSocket connections to AppSync Event API endpoints.
 */
@Injectable({
  providedIn: "root",
})
export class WebSocketEventService implements OnDestroy {
  // WebSocket connection
  private ws: WebSocket | null = null;

  // Dependencies
  private readonly zone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);

  // Connection state
  private currentSessionId: string | null = null;
  private currentBotCode: string | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private readonly reconnectDelay = 1000; // 1 second initial delay
  private readonly connectionTimeout = 10000; // 10 seconds
  private isDestroyed = false;
  private beforeUnloadListener: (() => void) | null = null;
  private reconnectTimeout: any = null;
  private connectionTimeoutId: any = null;
  private readonly destroy$ = new Subject<void>();

  // AppSync Event API endpoint (configurable via environment or setEndpoint/setApiKey)
  private wsEndpoint = environment.chatEvent?.wsEndpoint ?? "";
  private httpEndpoint = (environment.chatEvent?.wsEndpoint ?? "")
    .replace('wss://', 'https://')
    .replace('.appsync-realtime-api.', '.appsync-api.')
    .replace('/event/realtime', '/event');
  private apiKey = environment.chatEvent?.wsApiKey ?? "";

  // State management
  private readonly stateSubject = new BehaviorSubject<ConnectionState>(
    "DISCONNECTED",
  );
  public readonly state$ = this.stateSubject
    .asObservable()
    .pipe(takeUntil(this.destroy$));

  private get currentState(): ConnectionState {
    return this.stateSubject.getValue();
  }

  // Message subjects
  private readonly messageSubject = new Subject<WebSocketEventMessage>();

  // Public observables
  public readonly connected$: Observable<boolean>;
  public readonly messages$ = this.messageSubject
    .asObservable()
    .pipe(takeUntil(this.destroy$));

  constructor() {
    this.ngOnDestroy = this.ngOnDestroy.bind(this);

    // Initialize connected$ to track connection state changes
    this.connected$ = this.state$.pipe(
      map(
        (state: ConnectionState) =>
          state === "CONNECTED" ||
          state === "SUBSCRIBED" ||
          state === "SUBSCRIBING",
      ),
      takeUntil(this.destroy$),
    );

    // Set up browser cleanup listeners
    this.setupBrowserCleanupListeners();
  }

  /**
   * Sets the API key for authentication
   */
  public setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }

  /**
   * Sets the WebSocket endpoint URL and derives the HTTP endpoint from it
   */
  public setEndpoint(endpoint: string): void {
    this.wsEndpoint = endpoint;
    
    // Derive HTTP endpoint from WebSocket endpoint
    // Transform: wss://xxx.appsync-realtime-api.domain/event/realtime 
    //         -> https://xxx.appsync-api.domain/event
    this.httpEndpoint = endpoint
      .replace('wss://', 'https://')
      .replace('.appsync-realtime-api.', '.appsync-api.')
      .replace('/event/realtime', '/event');
  }

  /**
   * Updates the current connection state and notifies subscribers
   */
  private setState(newState: ConnectionState): void {
    if (this.currentState === newState) {
      return;
    }

    this.zone.run(() => this.stateSubject.next(newState));
  }

  /**
   * Gets the current session ID
   */
  public get sessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Gets the current bot code
   */
  public get botCode(): string | null {
    return this.currentBotCode;
  }

  /**
   * Connects to the AppSync Event API WebSocket
   */
  connect(sessionId: string, botCode: string): void {
    // Skip if already in the process of connecting/connected with same session
    if (
      ["CONNECTING", "CONNECTED", "SUBSCRIBING", "SUBSCRIBED"].includes(
        this.currentState,
      )
    ) {
      if (
        this.currentSessionId === sessionId &&
        botCode === this.currentBotCode
      ) {
        return;
      }
      // Disconnect if session or bot is different
      this.disconnect();
    }

    // Clear any pending timeouts
    this.clearTimeouts();

    this.currentBotCode = botCode;
    this.currentSessionId = sessionId;
    this.setState("CONNECTING");

    const { url, protocols } = this.createConnectionUrl();

    try {
      this.ws = new WebSocket(url, protocols);

      // Set up connection timeout
      this.connectionTimeoutId = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close(4000, "Connection timeout");
        }
      }, this.connectionTimeout);

      this.ws.onopen = () => this.handleOpen(sessionId, botCode);
      this.ws.onmessage = (event) => this.handleMessage(event);
      this.ws.onerror = (error) => this.handleError(error);
      this.ws.onclose = (event) => this.handleClose(event, sessionId, botCode);
    } catch (error) {
      this.clearTimeouts();
      this.setState("DISCONNECTED");
      this.attemptReconnect(sessionId, botCode);
    }
  }

  private createConnectionUrl(): { url: string; protocols: string[] } {
    // CRITICAL: Use HTTP endpoint host, NOT WebSocket endpoint host
    const httpUrl = new URL(this.httpEndpoint);

    // Create header JSON with host from HTTP endpoint and API key
    const headerJson = JSON.stringify({
      host: httpUrl.host,
      'x-api-key': this.apiKey,
    });

    // Encode to Base64 URL-safe
    let base64 = btoa(headerJson)
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
    
    // Remove padding '=' characters safely without vulnerable regex
    while (base64.endsWith('=')) {
      base64 = base64.slice(0, -1);
    }

    // AppSync Event API requires two subprotocols
    const protocols = [
      `header-${base64}`,        // Authentication protocol
      'aws-appsync-event-ws'     // AppSync Events protocol
    ];

    return { url: this.wsEndpoint, protocols };
  }

  private handleOpen(sessionId: string, botCode: string): void {
    this.zone.run(() => {
      // Clear connection timeout
      this.clearTimeouts();

      this.reconnectAttempts = 0;
      this.setState("CONNECTED");
      
      // Send connection_init message to start the handshake
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const initMessage = {
          type: "connection_init"
        };
        this.ws.send(JSON.stringify(initMessage));
      }
    });
  }

  private handleMessage(event: MessageEvent): void {
    this.zone.run(() => {
      try {
        const data = JSON.parse(event.data);
        
        this.processMessageByType(data);
      } catch (error) {
      }
    });
  }

  private processMessageByType(data: any): void {
    switch (data.type) {
      case "connection_ack":
        this.handleConnectionAck();
        break;
      case "subscribe_success":
        this.handleSubscribeSuccess();
        break;
      case "ka":
        this.handleKeepAlive();
        break;
      case "error":
        this.handleErrorMessage(data);
        break;
      case "data":
        this.handleDataMessage(data);
        break;
      default:
    }
  }

  private handleConnectionAck(): void {
    this.subscribeToChannel();
  }

  private handleSubscribeSuccess(): void {
    this.setState("SUBSCRIBED");
  }

  private handleKeepAlive(): void {
    // Keep-alive messages are handled automatically, no action needed
  }

  private handleErrorMessage(data: any): void {
    if (data.errors && Array.isArray(data.errors)) {
      // Error handling will be implemented in future iterations
    }
  }

  private handleDataMessage(data: any): void {
    if (!data.event) {
      return;
    }

    try {
      const eventData = this.parseEventData(data.event);
      
      const messageToEmit = this.createMessageToEmit(data.id, eventData);
      
      this.messageSubject.next(messageToEmit);
    } catch (error) {
    }
  }

  private parseEventData(eventString: any): any {
    return typeof eventString === 'string' 
      ? JSON.parse(eventString) 
      : eventString;
  }

  private createMessageToEmit(id: string, eventData: any): WebSocketEventMessage {
    const eventContent = eventData.message || eventData;
    
    return {
      id,
      type: "data",
      event: eventContent
    };
  }

  /**
   * Subscribes to the AppSync Event API channel after connection_ack
   */
  private subscribeToChannel(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!this.currentBotCode || !this.currentSessionId) {
      return;
    }

    // Channel format: /chatbots/{bot-code}/{session-id}
    const channel = `/chatbots/${this.currentBotCode}/${this.currentSessionId}`;

    const subscribeMessage = {
      type: "subscribe",
      id: `sub-${Date.now()}`,
      channel: channel,
      authorization: {
        "x-api-key": this.apiKey
      }
    };

    this.setState("SUBSCRIBING");

    try {
      this.ws.send(JSON.stringify(subscribeMessage));
    } catch (error) {
      this.setState("CONNECTED");
    }
  }

  private handleError(error: Event): void {
    this.zone.run(() => {
      if (
        this.currentState !== "DISCONNECTED" &&
        this.currentState !== "DISCONNECTING"
      ) {
        this.setState("DISCONNECTED");
      }
    });
  }

  private handleClose(event: CloseEvent, sessionId: string, botCode: string): void {
    this.zone.run(() => {
      // Clear any pending timeouts
      this.clearTimeouts();

      // Reset WebSocket reference
      this.ws = null;
      this.setState("DISCONNECTED");

      // Attempt to reconnect if this wasn't an intentional disconnect
      const shouldReconnect =
        !this.isDestroyed &&
        event.code !== 1000 &&
        this.reconnectAttempts < this.maxReconnectAttempts &&
        this.currentSessionId === sessionId;

      if (shouldReconnect) {
        this.attemptReconnect(sessionId, botCode);
      }
    });
  }

  /**
   * Disconnects the WebSocket connection
   */
  disconnect(): void {
    if (this.ws) {
      this.setState("DISCONNECTING");

      try {
        // Remove all event listeners before closing
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
        
        this.ws.close(1000, "Client disconnected");
      } catch (error) {
      } finally {
        this.ws = null;
        this.setState("DISCONNECTED");
      }
    }
  }

  /**
   * Attempts to reconnect to the WebSocket with exponential backoff
   */
  private attemptReconnect(sessionId: string, botCode: string): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.reconnectAttempts = 0;
      return;
    }

    this.reconnectAttempts++;

    const baseDelay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      30000,
    );

    // Use crypto.getRandomValues for secure random number generation
    const randomArray = new Uint32Array(1);
    crypto.getRandomValues(randomArray);
    const randomValue = randomArray[0] / 0xffffffff; // Normalize to [0, 1]
    const jitter = baseDelay * 0.2 * (randomValue * 2 - 1);
    const delay = Math.max(100, baseDelay + jitter);

    this.reconnectTimeout = setTimeout(() => {
      if (this.isDestroyed) {
        return;
      }

      if (this.currentSessionId !== sessionId) {
        return;
      }

      this.connect(sessionId, botCode);
    }, delay);
  }

  ngOnDestroy(): void {
    if (this.isDestroyed) {
      return;
    }

    this.isDestroyed = true;

    this.destroy$.next();
    this.destroy$.complete();

    this.disconnect();
    this.cleanupBrowserListeners();

    this.stateSubject.complete();
    this.messageSubject.complete();

    this.clearTimeouts();
  }

  private clearTimeouts(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.connectionTimeoutId) {
      clearTimeout(this.connectionTimeoutId);
      this.connectionTimeoutId = null;
    }
  }

  private setupBrowserCleanupListeners(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.beforeUnloadListener = () => {
      this.cleanupImmediate();
    };

    window.addEventListener("beforeunload", this.beforeUnloadListener);
  }

  private cleanupImmediate(): void {
    this.isDestroyed = true;

    if (this.ws) {
      try {
        if (this.ws.readyState === WebSocket.OPEN) {
          this.ws.close(1000, "Page unload");
        }
      } catch (error) {
      }
      this.ws = null;
    }
  }

  private cleanupBrowserListeners(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.beforeUnloadListener) {
      window.removeEventListener("beforeunload", this.beforeUnloadListener);
      this.beforeUnloadListener = null;
    }
  }
}
